import { Observable, type ReadonlyObservable } from "@/lib/studio/observable";
import { type PerfProfile, type PerfTier, profileForTier } from "./perf-profile";

/**
 * Reactive holder of the active `PerfProfile`.
 *
 * Two layers of state live in this store:
 * - **base profile** : decided by detection or by the user override
 *   (`setBase`). This is the "true" target for the device.
 * - **active profile** : `base` minus any auto-downgrade applied at runtime
 *   by the perf coordinator (`applyAutoDowngrade`). Consumers read this.
 *
 * Keeping the two separate lets us expose a "Restore" affordance in the
 * settings without losing track of what the user originally asked for.
 */
export class PerfProfileStore {
  private base: PerfProfile;
  private readonly active: Observable<PerfProfile>;
  private downgraded = false;

  constructor(initial: PerfProfile) {
    this.base = initial;
    this.active = new Observable<PerfProfile>(initial);
  }

  get value(): PerfProfile {
    return this.active.value;
  }

  get baseValue(): PerfProfile {
    return this.base;
  }

  get readonly(): ReadonlyObservable<PerfProfile> {
    return this.active;
  }

  subscribe(listener: (profile: PerfProfile) => void): () => void {
    return this.active.subscribe(listener);
  }

  /**
   * Replace the base profile (and consequently the active one if no
   * downgrade is in effect). Used when the user picks a tier in the
   * settings or — at boot — when detection runs.
   */
  setBase(tier: PerfTier, source: PerfProfile["source"]): void {
    const next = profileForTier(tier, source);
    this.base = next;

    // Switching the base wipes any active downgrade — the user has spoken.
    this.downgraded = false;
    this.active.set(next);
  }

  /**
   * Apply a partial override on top of the base profile. Used by the perf
   * coordinator when it detects sustained jank. Idempotent — calling
   * twice with the same fields is a no-op.
   */
  applyAutoDowngrade(partial: {
    audio?: Partial<PerfProfile["audio"]>;
    effects?: Partial<PerfProfile["effects"]>;
    render?: Partial<PerfProfile["render"]>;
  }): void {
    const next: PerfProfile = {
      ...this.base,
      audio: { ...this.base.audio, ...partial.audio },
      effects: { ...this.base.effects, ...partial.effects },
      render: { ...this.base.render, ...partial.render },
    };

    this.downgraded = true;
    this.active.set(next);
  }

  /** Wipe the runtime downgrade and restore whatever the base profile holds. */
  restoreBase(): void {
    if (!this.downgraded) {
      return;
    }

    this.downgraded = false;
    this.active.set(this.base);
  }

  isAutoDowngraded(): boolean {
    return this.downgraded;
  }
}
